import React, { useEffect, useReducer, useRef, useState } from 'react';
import './contact-details-form.scss';
import { FMDropdown, FMInput } from '@own/fds-react';
import CountryDialCodePicker, {
    CountryDialCodePickerContent,
    CountryDialCodePickerOptions,
} from '../../../../common/country-dial-code-picker/country-dial-code-picker';
import {
    getInitialPersonalDetailState,
    useOSBStep,
    usePersonalDetailStep,
} from '../../../../../hooks/owners-osb';
import { personalDetailReducer } from '../../../../../reducers/osb-reducers';
import { PersonalDetail } from '../../../../../models/osb-model/osb-personal-details';
import { useCountryDialCodePicker } from '../../../../../hooks/use-country-dial-code-picker';
import OsbUtilService from '../../../../../services/osb-service/osb-util-service/osb-util-service';
import { useOSBContentFragment } from '../../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';
import { EMAIL_REGEX_PATTERN } from '../../osb-v3-constant';
import useUserProfile from '../../../../../hooks/use-user-profile';
import { userProfileData } from '../../../owners-osb/osb-utils';
import ContactDetailsFormSkeleton from '../contact-details-form-skeleton/contact-details-form-skeleton';

interface ContactDetailsFormProps {
    onValidityChange: (isValid: boolean) => void;
}

type Errors = {
    firstName?: string;
    lastName?: string;
    contactNumber?: string;
    email?: string;
};

const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
    onValidityChange,
}) => {
    const contactDetailsContent =
        useOSBContentFragment('common/osb-v3', 'contact-details-step') || {};
    const isLoading = !contactDetailsContent?.yourContactDetailsTitle;

    const countryDialCodeContent = useCountryDialCodePicker();
    const { osbStep } = useOSBStep();
    const hasPrepopulated = useRef(false);
    const profile = useUserProfile();

    const [
        defaultMarketCodeToDisplay,
        setDefaultMarketCodeToDisplay,
    ] = useState<string>('');
    const [countryCodeData, setCountryCodeData] = useState<
        CountryDialCodePickerContent
    >();
    const appConfig = OsbUtilService.getAppConfiguration();
    const { osbPersonalDetail, setOSBPersonalDetail } = usePersonalDetailStep();
    const [personalDetail, setPersonalDetail] = useReducer(
        personalDetailReducer,
        getInitialPersonalDetailState()
    );

    const setComponentPayload = (
        payload:
            | { [key: string]: string | boolean | CountryDialCodePickerOptions }
            | PersonalDetail
    ): void => {
        setPersonalDetail({
            type: 'SET',
            payload: payload,
            localStorageExpiry: osbStep.localStorageExpiry,
        });
    };
    const [errors, setErrors] = useState<Errors>({});

    const titleOptions = Array.isArray(contactDetailsContent?.titles)
        ? contactDetailsContent?.titles.map(title => ({
              label: title,
              value: title,
          }))
        : [];

    function validateField(fieldName: string, value: string): string {
        let error = '';

        switch (fieldName) {
            case 'firstName':
                if (!value?.trim()) {
                    error = contactDetailsContent?.firstNameRequiredFieldAlertMessage as string;
                }
                break;
            case 'lastName':
                if (!value?.trim()) {
                    error = contactDetailsContent?.lastNameRequiredFieldAlertMessage as string;
                }
                break;
            case 'contactNumber':
                if (!value?.trim()) {
                    error = contactDetailsContent?.contactNumberRequiredFieldAlertMessage as string;
                } else if (!isValidContactNumber(value)) {
                    error = contactDetailsContent?.contactNumberInvalidFieldAlertMessage as string;
                }
                break;
            case 'email':
                if (!value?.trim()) {
                    error = contactDetailsContent?.emailRequiredFieldAlertMessage as string;
                } else if (!isValidEmail(value)) {
                    error = contactDetailsContent?.emailInvalidFieldAlertMessage as string;
                }
                break;
            default:
                break;
        }

        return error;
    }

    function isValidEmail(email: string): boolean {
        const emailRegex = EMAIL_REGEX_PATTERN;
        return emailRegex.test(email);
    }

    function isValidContactNumber(contactNumber: string): boolean {
        //const pattern = contactDetailsContent?.contactNumberRegexForValidation as string;
        const pattern = '^((\\+|00)44)?\\d{7,11}$';
        if (contactNumber.length <= 0 || !RegExp(pattern).exec(contactNumber)) {
            return false;
        } else {
            return true;
        }
    }

    const handleTitleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setComponentPayload({
            title: event.target.value,
        });
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setComponentPayload({ [name]: value?.trimStart() });
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: validateField(name, value),
        }));
    };
    const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: validateField(name, value),
        }));
    };
    useEffect(() => {
        if (profile?.profile && !hasPrepopulated.current) {
            const customerDetails = userProfileData(profile.profile);
            setOSBPersonalDetail(customerDetails, false);
            hasPrepopulated.current = true;
        }
    }, [profile]);
    useEffect(() => {
        const allFieldsFilled =
            personalDetail.firstName?.trim() &&
            personalDetail.lastName?.trim() &&
            personalDetail.contactNumber?.trim() &&
            personalDetail.email?.trim();
        const hasNoErrors =
            !errors.firstName &&
            !errors.lastName &&
            !errors.email &&
            !errors.contactNumber;

        const isFormValid = !!allFieldsFilled && hasNoErrors;
        isFormValid ?? setOSBPersonalDetail(personalDetail, false);
        onValidityChange(isFormValid);
    }, [osbPersonalDetail, errors, onValidityChange]);

    useEffect(() => {
        if (countryDialCodeContent?.dropdownOptions) {
            const contactNumber = osbPersonalDetail.contactNumber;
            const countryCode =
                contactNumber.length > 0
                    ? getCountryCodeFromContactNumber(contactNumber)
                    : '';

            setDefaultMarketCodeToDisplay(countryCode || appConfig.countryCode);

            setCountryCodeData(countryDialCodeContent);
        }

        if (osbPersonalDetail) {
            setPersonalDetail({
                type: 'SET',
                payload: osbPersonalDetail,
            });
        }
    }, [countryDialCodeContent, osbPersonalDetail]);

    function handleSelectedCountry(
        countrySelected: CountryDialCodePickerOptions | undefined,
        initialLoad: boolean
    ) {
        if (countrySelected) {
            let contactNumber = countrySelected.countryDialCode;
            if (initialLoad && personalDetail.contactNumber.length > 0) {
                contactNumber = personalDetail.contactNumber.startsWith(
                    countrySelected.countryDialCode
                )
                    ? personalDetail.contactNumber
                    : `${countrySelected.countryDialCode}${personalDetail.contactNumber}`;
            }

            setComponentPayload({
                contactNumber,
                countryDialCodeObject: countrySelected,
            });
        }
    }

    function getCountryCodeFromContactNumber(contactNumber: string) {
        if (!countryDialCodeContent?.dropdownOptions) {
            return '';
        }

        const foundItem = countryDialCodeContent.dropdownOptions.find(item =>
            contactNumber.startsWith(item.countryDialCode)
        );

        return foundItem ? foundItem.countryCode : '';
    }
    if (isLoading) {
        return <ContactDetailsFormSkeleton />;
    }
    return (
        <div data-testid="contact-form" className="osb-v3-contact-details-form">
            <div className="osb-v3-contact-details-form__input">
                <div className="osb-v3-contact-details-form__title">
                    {contactDetailsContent?.yourContactDetailsTitle}
                </div>
                <label className="osb-v3-contact-details-form__label">
                    {contactDetailsContent?.titleLabel}
                </label>
                <FMDropdown
                    data-testid="title-dropdown"
                    onChange={handleTitleChange}
                    options={titleOptions}
                    value={personalDetail.title}
                    state="standard"
                    ariaLabel="Select your title"
                />
                <label
                    className="osb-v3-contact-details-form__label"
                    data-testid="first-name"
                >
                    {contactDetailsContent?.firstNameLabel}
                </label>
                {/* eslint-disable-next-line*/}
                <FMInput
                    ariaLabel="first-name-input"
                    name="firstName"
                    type="text"
                    className=""
                    value={personalDetail.firstName}
                    role="textbox"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required={true}
                />
                {errors.firstName && (
                    <div
                        data-testid="first-name-error"
                        className="osb-v3-error-message"
                    >
                        {errors.firstName}
                    </div>
                )}

                <label
                    className="osb-v3-contact-details-form__label"
                    data-testid="last-name"
                >
                    {contactDetailsContent?.lastNameLabel}
                </label>
                <FMInput
                    ariaLabel="last-name-input"
                    name="lastName"
                    type="text"
                    value={personalDetail.lastName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required={true}
                />
                {errors.lastName && (
                    <div
                        data-testid="last-name-error"
                        className="osb-v3-error-message"
                    >
                        {errors.lastName}
                    </div>
                )}

                <div className="osb-v3-contact-details-form__row">
                    <div className="osb-v3-contact-details-form__column">
                        <label
                            className="osb-v3-contact-details-form__label"
                            data-testid="country-code"
                        >
                            {contactDetailsContent?.countryCodeLabel}
                        </label>
                        <CountryDialCodePicker
                            data-testid="country-code-picker"
                            countryDialCodeContent={countryCodeData}
                            displayDefaultMarketCode={
                                defaultMarketCodeToDisplay
                            }
                            selectedCountry={handleSelectedCountry}
                        />
                    </div>
                    <div className="osb-v3-contact-details-form__column">
                        <label
                            className="osb-v3-contact-details-form__label"
                            data-testid="contact-number"
                        >
                            {contactDetailsContent?.contactNumberLabel}
                        </label>
                        <div className="osb-v3-contact-number-input">
                            <FMInput
                                ariaLabel="contact-number-input"
                                name="contactNumber"
                                type="text"
                                value={personalDetail.contactNumber}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                required={true}
                            />
                        </div>
                        {errors.contactNumber && (
                            <div
                                data-testid="contact-number-error"
                                className="osb-v3-error-message"
                            >
                                {errors.contactNumber}
                            </div>
                        )}
                    </div>
                </div>

                <label
                    className="osb-v3-contact-details-form__label"
                    data-testid="contact-email"
                >
                    {contactDetailsContent?.emailLabel}
                </label>
                <FMInput
                    ariaLabel="email-input"
                    name="email"
                    type="text"
                    value={personalDetail.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required={true}
                />
                {errors.email && (
                    <div
                        data-testid="email-error"
                        className="osb-v3-error-message"
                    >
                        {errors.email}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContactDetailsForm;
